/**
 * Servicio de WebSocket para comunicación con LLM
 * Basado en LoginWebSocket.cs y WebSocketService.cs
 */

import { ChatMessage, WebSocketConfig, PracticeMode, AccentType } from '../types';
import configService from './configService';
import llmCredentialsService from './llmCredentialsService';
import conversationSaverService from './conversationSaverService';
import academicService, { LessonObjective } from './academicService';
import academicEvaluationService from './academicEvaluationService';
import objectiveEvaluationService, { ObjectiveEvaluationResult } from './objectiveEvaluationService';
import assistantResponseEvaluationService, { AssistantResponseQuality } from './assistantResponseEvaluationService';
import promptModifierService from './promptModifierService';
import { assembleFullPrompt } from '../config/prompts';
import authService from './authService';
import { getVoiceConfig } from '../config/voiceConfig';
import axios from 'axios';

export interface WebSocketMessage {
  role: 'user' | 'assistant';
  content: string;
}

export class WebSocketService {
  /**
   * Verifica si un token JWT está expirado o cerca de expirar
   * @param token Token JWT a verificar
   * @param bufferSeconds Segundos de margen antes de considerar el token como expirado (default: 60)
   * @returns true si el token está expirado o cerca de expirar
   */
  private isTokenExpired(token: string, bufferSeconds: number = 60): boolean {
    try {
      if (!token || token.trim() === '') {
        return true;
      }

      // Dividir el token en sus tres partes (Header, Payload, Signature)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.warn('[WebSocketService] Token no tiene formato JWT válido');
        return true;
      }

      // Decodificar el Payload (parte 2 del token)
      const payload = tokenParts[1];
      let decodedPayload: string;
      try {
        // Agregar padding si es necesario para Base64
        let padded = payload;
        while (padded.length % 4) {
          padded += '=';
        }
        decodedPayload = atob(padded);
      } catch (error) {
        console.warn('[WebSocketService] Error al decodificar payload del token:', error);
        return true;
      }

      // Parsear el JSON del Payload
      let payloadData: any;
      try {
        payloadData = JSON.parse(decodedPayload);
      } catch (error) {
        console.warn('[WebSocketService] Error al parsear payload del token:', error);
        return true;
      }

      // Verificar si el atributo 'exp' está presente
      if (payloadData.exp) {
        const expirationTime = payloadData.exp * 1000; // Convertir a milisegundos
        const currentTime = Date.now();
        const bufferTime = bufferSeconds * 1000; // Convertir buffer a milisegundos
        
        // Considerar expirado si falta menos de bufferSeconds para expirar
        const isExpired = (expirationTime - currentTime) < bufferTime;
        
        if (isExpired) {
          const secondsUntilExpiry = Math.floor((expirationTime - currentTime) / 1000);
          console.log(`[WebSocketService] Token expira en ${secondsUntilExpiry} segundos (buffer: ${bufferSeconds}s)`);
        }
        
        return isExpired;
      } else {
        // Si no hay campo 'exp', considerar el token como válido pero loguear advertencia
        console.warn('[WebSocketService] Token no tiene campo "exp", no se puede verificar expiración');
        return false;
      }
    } catch (error) {
      console.error('[WebSocketService] Error al verificar expiración del token:', error);
      return true; // En caso de error, considerar como expirado para forzar renovación
    }
  }

  /**
   * Renueva el token de acceso usando el refreshToken
   */
  private async refreshAccessToken(): Promise<void> {
    const config = configService.getConfig();
    if (!config.refreshToken) {
      throw new Error('No hay refreshToken disponible para renovar el token');
    }

    const refreshUrl = `${config.baseUrlLLM}/api/v1/auth/refresh/`;
    const response = await axios.post<{ access: string; refresh?: string }>(
      refreshUrl,
      {
        refresh: config.refreshToken,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Actualizar el token en configService
    configService.setAccessToken(response.data.access);
    
    // Si la respuesta incluye un nuevo refreshToken, actualizarlo también
    if (response.data.refresh) {
      configService.setRefreshToken(response.data.refresh);
    }
    
    // Actualizar también en llmCredentialsService si tiene credenciales guardadas
    const credentials = llmCredentialsService.getCredentials();
    if (credentials) {
      credentials.accessToken = response.data.access;
      if (response.data.refresh) {
        credentials.refreshToken = response.data.refresh;
      }
    }
  }
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private isInitializing = false;
  private chathistory: ChatMessage[] = []; // Historial de chat
  
  // Colas para chunks (modelo Unity: 1:1 texto/audio)
  private messageQueue: string[] = []; // Cola de chunks de texto
  private audioQueue: Array<{ url: string; blob: Blob }> = []; // Cola de chunks de audio
  private accumulatedChunks = ''; // Acumulador para historial final
  // Almacenar blobs de audio para reproducir después (no revocar URLs inmediatamente)
  private audioBlobStore: Map<string, Blob> = new Map(); // Map<audioUrl, Blob>
  private pendingSendQueue: Array<{ payload: string; type: 'audio' | 'text' }> = []; // Cola de mensajes pendientes de enviar
  private onMessageCallback?: (message: ChatMessage) => void;
  // @ts-ignore - Se usa en onAudio() y offAudio()
  private onAudioCallback?: (audioUrl: string) => void;
  private onAvatarStateCallback?: (isHablando: boolean) => void;
  private onConnectionReadyCallback?: () => void; // Callback para notificar cuando la conexión esté lista
  private onChatHistoryClearedCallback?: () => void; // Callback para notificar cuando se limpia el historial
  
  // Estado interno para manejar LLM y transcripción
  private mainLLM: string = '';
  private mainModel: string = '';
  private backupLLM: string = '';
  private backupModel: string = '';
  private transcriptionModel: string = '';
  private canSendMessage: boolean = true;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private expectingTranscription: boolean = false; // Flag para detectar transcripción
  private isResponseInterrupted: boolean = false; // Flag para ignorar chunks de respuestas interrumpidas
  private interruptedResponseChunks: string[] = []; // Acumular chunks de respuesta interrumpida para detectar fin
  private interruptedAudioChunks: Blob[] = []; // Acumular audio de respuesta interrumpida
  private lastUserMessageWasEmpty: boolean = false; // Flag para indicar que el último mensaje del usuario fue vacío
  
  // Identificador único de sesión de chat
  private sessionId: string;
  
  // Control de reproducción de audio
  private isPlayingAudio: boolean = false;
  private currentAudioElement: HTMLAudioElement | null = null;
  private wasPlayingBeforeHidden: boolean = false; // Flag para saber si estaba reproduciendo antes de ocultar
  private visibilityChangeHandler: (() => void) | null = null;
  private isManualAudioPlaying: boolean = false; // Flag para saber si hay audio manual reproduciéndose
  private isClearingQueues: boolean = false; // Flag para ignorar mensajes entrantes durante limpieza

  // Estado para modo académico
  private academicMode: {
    isActive: boolean;
    level?: string;
    unit?: number;
    lesson?: number;
    objectives?: LessonObjective[];
    personalizedTask?: string; // Tarea personalizada para esta lección
    userEmail?: string;
    onObjectiveUpdate?: (objectiveDescription: string, isCorrect: boolean) => void;
    onEvaluationComplete?: () => void; // Callback cuando se completa la evaluación de la rúbrica
  } = {
    isActive: false,
  };

  // Guardar el prompt para poder enviarlo como mensaje inicial
  private currentPrompt: string = '';

  constructor() {
    // Generar un ID único de sesión basado en timestamp y número aleatorio
    this.sessionId = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Configurar listener para detectar cuando la página pierde/recupera el foco
    this.setupVisibilityHandlers();
  }

  /**
   * Configurar handlers para detectar cuando la página pierde/recupera el foco
   */
  private setupVisibilityHandlers(): void {
    if (typeof document === 'undefined') return; // SSR safety
    
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        // Página oculta - pausar audio
        this.pauseAudioOnHidden();
      } else {
        // Página visible - reanudar audio si estaba reproduciendo
        this.resumeAudioOnVisible();
        
        // Verificar si el WebSocket está desconectado y reconectar si es necesario
        this.checkAndReconnectOnVisible();
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Verificar y reconectar WebSocket cuando la página vuelve a ser visible
   */
  private checkAndReconnectOnVisible(): void {
    // Verificar si el WebSocket está cerrado o no existe
    const isDisconnected = !this.ws || 
                          this.ws.readyState === WebSocket.CLOSED || 
                          this.ws.readyState === WebSocket.CLOSING;
    
    // Solo reconectar si:
    // 1. El WebSocket está desconectado
    // 2. No estamos ya intentando conectar
    // 3. No hemos excedido los intentos de reconexión
    if (isDisconnected && !this.isConnecting && !this.isInitializing && 
        this.reconnectAttempts < this.maxReconnectAttempts) {
      // Resetear contador de intentos para permitir reconexión
      this.reconnectAttempts = 0;
      
      // Intentar reconectar
      this.initialize().catch((error) => {
        // console.error(`[${this.sessionId}] [Reconexión] Error al reconectar:`, error);
      });
    }
  }

  /**
   * Pausar audio cuando la página se oculta
   */
  private pauseAudioOnHidden(): void {
    if (this.currentAudioElement && !this.currentAudioElement.paused) {
      this.wasPlayingBeforeHidden = true;
      this.currentAudioElement.pause();
    }
  }

  /**
   * Reanudar audio cuando la página vuelve a ser visible
   */
  private resumeAudioOnVisible(): void {
    if (this.wasPlayingBeforeHidden && this.currentAudioElement) {
      this.wasPlayingBeforeHidden = false;
      
      this.currentAudioElement.play().catch((error) => {
        // console.error(`[${this.sessionId}] [Audio] Error al reanudar reproducción:`, error);
        // Si falla, continuar con el siguiente audio
        this.isPlayingAudio = false;
        this.currentAudioElement = null;
        if (this.messageQueue.length > 0 && this.audioQueue.length > 0) {
          this.tryProcessNextChunkPair();
        }
      });
    } else if (this.wasPlayingBeforeHidden && !this.currentAudioElement && this.messageQueue.length > 0 && this.audioQueue.length > 0) {
      // Si el audio se perdió pero había pares en cola, continuar con el siguiente
      this.wasPlayingBeforeHidden = false;
      this.tryProcessNextChunkPair();
    }
  }

  /**
   * Obtener el ID de sesión actual
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Construir contexto completo incluyendo system prompt + historial
   */
  private buildFullContext(): string {
    const prompt = this.currentPrompt || '';
    const processedHistory = this.concatenateAssistantMessages(this.chathistory);
    const fullContext = [
      { role: 'system', content: prompt },
      ...processedHistory
    ];
    return JSON.stringify(fullContext);
  }

  /**
   * Concatenar mensajes assistant consecutivos para el contexto
   * Concatena chunks de la misma respuesta SIEMPRE
   * NO concatena solo cuando hay un mensaje no válido del usuario entre ellos (usando el flag)
   */
  private concatenateAssistantMessages(messages: ChatMessage[]): Array<{ role: string; content: string }> {
    return messages.reduce((acc: Array<{ role: string; content: string }>, message, index) => {
      if (index === 0) {
        // Primer mensaje siempre se agrega
        acc.push({ role: message.role, content: message.content });
      } else {
        const previousMessage = acc[acc.length - 1];
        const previousOriginalMessage = messages[index - 1];
        
        // Si ambos son assistant, normalmente se concatenan (son chunks de la misma respuesta)
        if (message.role === 'assistant' && previousMessage.role === 'assistant') {
          // Si el flag está activo Y son consecutivos en el original, significa que hubo un mensaje no válido entre ellos
          // Son respuestas diferentes, NO concatenar
          if (this.lastUserMessageWasEmpty && previousOriginalMessage.role === 'assistant') {
            // NO concatenar: son respuestas diferentes después de un mensaje no válido
            acc.push({ role: message.role, content: message.content });
            // Resetear flag después de procesar el primer mensaje
            this.lastUserMessageWasEmpty = false;
          } else {
            // Concatenar normalmente (chunks de la misma respuesta)
            previousMessage.content = `${previousMessage.content}\n\n${message.content}`;
          }
        } else {
          // Si es user o es el primer assistant después de un user, agregar normalmente
          acc.push({ role: message.role, content: message.content });
        }
      }
      return acc;
    }, []);
  }

  /**
   * Inicializar conexión WebSocket
   */
  async initialize(): Promise<void> {
    // Protección contra múltiples inicializaciones
    if (this.isInitializing || this.isConnecting) {
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    if (this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.isInitializing = true;
    this.isConnecting = true;

    try {
      // Obtener credenciales LLM si no están disponibles
      let finalCredentials = llmCredentialsService.getCredentials();
      
      if (!finalCredentials) {
        // Hacer login y obtener credenciales
        finalCredentials = await llmCredentialsService.loginAndGetCredentials();
      }

      // VALIDACIÓN CRÍTICA: Asegurar que tenemos el token antes de continuar
      if (!finalCredentials) {
        throw new Error('No se pudieron obtener las credenciales después del login');
      }

      if (!finalCredentials.accessToken || finalCredentials.accessToken.trim() === '') {
        throw new Error('El token de acceso está vacío o no está disponible');
      }

      // Verificar si el token está expirado o cerca de expirar
      const tokenExpired = this.isTokenExpired(finalCredentials.accessToken);
      if (tokenExpired) {
        console.log('[WebSocketService] ⚠️ Token expirado o cerca de expirar, renovando...');
        try {
          // Intentar renovar el token usando refreshToken
          await this.refreshAccessToken();
          // Obtener las credenciales actualizadas
          finalCredentials = llmCredentialsService.getCredentials();
          if (!finalCredentials || !finalCredentials.accessToken) {
            // Si no se pudo renovar, hacer login de nuevo
            console.log('[WebSocketService] ⚠️ No se pudo renovar el token, haciendo login de nuevo...');
            finalCredentials = await llmCredentialsService.loginAndGetCredentials();
          } else {
            console.log('[WebSocketService] ✅ Token renovado exitosamente');
          }
        } catch (refreshError) {
          console.warn('[WebSocketService] ⚠️ Error al renovar token, haciendo login de nuevo:', refreshError);
          // Si falla la renovación, hacer login de nuevo
          finalCredentials = await llmCredentialsService.loginAndGetCredentials();
        }
      }

      const config = configService.getConfig();

      // Configurar modelos disponibles
      const availableModels = config.testingPhase
        ? [
            ['gemini', 'gemini-2.0-flash'],
            ['gpt', 'gpt-4o-2024-08-06'],
            ['claude', 'claude-3-sonnet-20240229'],
          ]
        : [
            ['gpt', 'gpt-4o-2024-08-06'],
            ['gpt', 'gpt-3.5-turbo'],
            ['claude', 'claude-3-sonnet-20240229'],
          ];

      const LLMsJson = JSON.stringify(availableModels);
      const transcriptionModel = 'gemini-2.0-flash';

      // Obtener prompt principal
      // IMPORTANTE: Obtener el promptReference directamente del configService para asegurar que esté actualizado
      // Esto es crítico porque el promptReference puede cambiar entre el inicio de initialize() y aquí
      const currentConfig = configService.getConfig();
      const promptReference = currentConfig.promptReference;

      if (!currentConfig.fullPrompt) {
        // Obtener eventos actuales de la Alianza Francesa (S3) para el prompt
        const EVENTOS_AF_URL = 'https://alianza-eventos-767968023146.s3.us-east-1.amazonaws.com/eventos-actuales.json';
        let eventos_obtenidos = '';
        try {
          const res = await fetch(EVENTOS_AF_URL);
          if (res.ok) {
            const data = await res.json();
            eventos_obtenidos = typeof data.eventos !== 'undefined'
              ? JSON.stringify(data.eventos)
              : JSON.stringify(data);
          }
        } catch {
          // Continuar sin eventos si falla la petición
        }

        // Obtener city y role del usuario desde Apolearn
        const apolearnData = authService.getApolearnUserData();
        const userCity = (apolearnData?.user as any)?.city || '';
        const userRole = apolearnData?.role || '';

        // Ensamblar prompt localmente desde prompts.ts (ya no se llama a la API de prompts)
        const dynamicContext: import('../config/prompts').DynamicPromptContext = {
          userFullName: currentConfig.userFullName || '',
          userCity,
          userRole,
          colombiaTime: new Date(),
          eventosAF: eventos_obtenidos,
          summaryPrevAssistant: '',
        };

        const personalityType = (currentConfig.personalityType || 'Amigable') as import('../types').PersonalityType;
        const functionalityType = (currentConfig.functionalityType || 'Conversadora') as import('../types').FunctionalityType;
        const mcerLevel = (currentConfig.mcerLevel || 'B1') as import('../types').MCERLevel;

        let fullPrompt = assembleFullPrompt(dynamicContext, personalityType, functionalityType, mcerLevel);

        // Si hay modo académico activo, obtener progreso y agregar contexto de tarea
        let objectivesProgress: any[] = [];
        if (this.academicMode.isActive && this.academicMode.objectives && this.academicMode.userEmail) {
          const { level, unit, lesson, objectives, userEmail } = this.academicMode;
          
          try {
            const progress = await academicService.getUserLessonProgress(
              userEmail,
              level!,
              unit!,
              lesson!
            );
            
            if (progress && progress.objectives_status) {
              objectivesProgress = Object.entries(progress.objectives_status).map(([description, status]: [string, any]) => ({
                description,
                completed: status.completed || false,
                correct_count: status.correct_count || 0,
                total_correct: status.total_correct || 0,
              }));
            }
          } catch (error) {
            // Continuar sin progreso si hay error
          }
          
          // Concatenar contexto de tarea personalizada si existe
          if (this.academicMode.personalizedTask) {
            let objectivesStatusText = '';
            if (objectivesProgress.length > 0) {
              const completedObjectives = objectivesProgress
                .filter((obj: any) => obj.completed)
                .map((obj: any) => obj.description);
              const pendingObjectives = objectivesProgress
                .filter((obj: any) => !obj.completed)
                .map((obj: any) => obj.description);
              
              if (completedObjectives.length > 0) {
                objectivesStatusText += `\n\nOBJETIVOS YA CUMPLIDOS (no te enfoques en estos, ya fueron completados):\n${completedObjectives.map((desc: string, idx: number) => `${idx + 1}. ${desc}`).join('\n')}`;
              }
              
              if (pendingObjectives.length > 0) {
                objectivesStatusText += `\n\nOBJETIVOS PENDIENTES (enfócate en estos durante la conversación):\n${pendingObjectives.map((desc: string, idx: number) => `${idx + 1}. ${desc}`).join('\n')}`;
              }
            }
            
            const taskInstructions = `

---

## CONTEXTO DE LA ACTIVIDAD ACADÉMICA

### TAREA (Situación a Simular):
El estudiante debe realizar la siguiente tarea/situación:
${this.academicMode.personalizedTask}

### OBJETIVOS (Lo que debe cumplir):
Los objetivos son los criterios específicos que el estudiante debe demostrar durante la conversación. ${objectivesStatusText ? `\n${objectivesStatusText}` : ''}

**IMPORTANTE - Diferencia entre TAREA y OBJETIVOS:**
- **TAREA**: Es la situación o escenario que el estudiante debe simular. Es el contexto general de la conversación.
- **OBJETIVOS**: Son los criterios específicos que el estudiante debe cumplir durante la conversación. El estudiante completa la tarea cuando cumple TODOS los objetivos.

**COMPLETACIÓN DE LA ACTIVIDAD:**
La actividad se considera completada cuando el estudiante cumple TODOS los objetivos durante la ejecución de la tarea.

INSTRUCCIONES CRÍTICAS:
1. Si la tarea no lo especifica, inicia con proponerle al estudiante una forma dinámica de cumplir con la tarea (puede ser un juego de rol, una situación, etc.)
2. Si se trata de un juego de rol: debe ser natural y únicamente en francés. No des explicaciones ni correcciones durante el juego de rol. El usuario se encarga de decirte cuándo detenerse. No coloques viñetas, ni paréntesis ni indicaciones.
3. ENFÓCATE PRINCIPALMENTE EN LOS OBJETIVOS PENDIENTES. Si un objetivo ya fue cumplido, no necesitas volver a trabajarlo.

Recuerda: La tarea es el contexto/situación, pero los objetivos son lo que realmente debe cumplir el estudiante.`;

            fullPrompt = fullPrompt + taskInstructions;
          }
        }

        configService.setFullPrompt(fullPrompt);
      }

      // Obtener config actualizado después de obtener el prompt
      const updatedConfig = configService.getConfig();
      if (!updatedConfig.fullPrompt || updatedConfig.fullPrompt.trim() === '') {
        throw new Error('No se pudo obtener el prompt completo');
      }

      // VALIDACIÓN FINAL: Verificar token una vez más antes de conectar
      if (!finalCredentials.accessToken || finalCredentials.accessToken.trim() === '') {
        throw new Error('Token de acceso no disponible al momento de conectar WebSocket');
      }

      // Obtener configuración de voz según modo y acento
      const practiceMode = updatedConfig.practiceMode as PracticeMode || 'conversacional';
      const accent = updatedConfig.accent as AccentType || 'Francia';
      const voiceConfig = getVoiceConfig(practiceMode, accent);

      // Inicializar WebSocket
      // Nota: En producción, esto se haría a través de JavaScript externo
      // Por ahora, simulamos la conexión
      this.connectWebSocket({
        tokenUrl: finalCredentials.accessToken,
        toConnect: finalCredentials.toConnect || '',
        prompt: updatedConfig.fullPrompt,
        voice: voiceConfig.voice,
        lenguaje: voiceConfig.language,
        LLMsJson,
        transcriptionModel,
        testingWebSocket: updatedConfig.testingWebSocket,
      });
    } catch (error) {
      // console.error('Error al inicializar WebSocket:', error);
      this.isConnecting = false;
      this.isInitializing = false;
      throw error;
    } finally {
      // Asegurar que isInitializing se resetee después de la inicialización
      // (pero mantener isConnecting si la conexión está en progreso)
      if (this.ws?.readyState !== WebSocket.CONNECTING) {
        this.isInitializing = false;
      }
    }
  }

  private connectWebSocket(config: WebSocketConfig): void {
    const { tokenUrl, toConnect, prompt, voice, lenguaje, LLMsJson, transcriptionModel, testingWebSocket } = config;
    
    // Guardar el prompt para poder enviarlo como mensaje inicial después
    this.currentPrompt = prompt;
    
    // VALIDACIÓN FINAL: Asegurar que tenemos el token antes de conectar
    if (!tokenUrl) {
      this.isConnecting = false;
      throw new Error('Token de acceso no disponible para conectar WebSocket (undefined/null)');
    }

    if (tokenUrl.trim() === '') {
      this.isConnecting = false;
      throw new Error('Token de acceso no disponible para conectar WebSocket (vacío)');
    }
    
    // Parsear LLMs
    const LLMs = JSON.parse(LLMsJson);
    this.mainLLM = LLMs[0][0];
    this.mainModel = LLMs[0][1];
    this.backupLLM = LLMs[1] ? LLMs[1][0] : LLMs[0][0];
    this.backupModel = LLMs[1] ? LLMs[1][1] : LLMs[0][1];
    this.transcriptionModel = transcriptionModel || '';
    
    // Obtener configuración para determinar si usar servidor de desarrollo o producción
    // Si testingPhase es false (producción), siempre usar servidor de producción
    // independientemente del valor de testingWebSocket
    const appConfig = configService.getConfig();
    const useDevServer = appConfig.testingPhase && testingWebSocket;
    
    // Construir URL del WebSocket con parámetros codificados
    const whisperPrompt = 'Umm, Amm, Emm, hmm... Okay, Ahh, Ehh, Ohh, Galia, Comment ça va';
    const baseUrl = useDevServer
      ? 'wss://llm-dev.aitopstaff.com/ws/completion/'
      : 'wss://llm.aitopstaff.com/ws/completion/';
    
    const params = new URLSearchParams({
      token: tokenUrl,
      lang: lenguaje,
      name: voice,
      tenant: toConnect || '',
      environment: useDevServer ? 'DEV' : 'PRO',
      stt_service: 'llm',
      whisper_prompt: whisperPrompt,
    });
    
    const wsUrl = `${baseUrl}?${params.toString()}`;
    
    this.ws = new WebSocket(wsUrl);
    
    this.ws.onopen = () => {
      // Verificar que el WebSocket esté realmente abierto
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Procesar cola de mensajes pendientes
        if (this.pendingSendQueue.length > 0) {
          const queueCopy = [...this.pendingSendQueue];
          this.pendingSendQueue = [];
          
          queueCopy.forEach(({ payload: queuedPayload }) => {
            this.ws!.send(queuedPayload);
          });
        }
        
        // Heartbeat cada 30 segundos
        this.heartbeatInterval = setInterval(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
              message: 'heartbeat',
              timestamp: new Date().toLocaleString(),
            }));
          }
        }, 30000);
        
        this.isConnecting = false;
        this.isInitializing = false;
        this.reconnectAttempts = 0;
        
        // Enviar mensaje inicial automáticamente cuando la conexión esté lista
        if (this.currentPrompt && this.currentPrompt.trim() !== '') {
          this.sendInitialMessage();
        }
        
        // Notificar que la conexión está lista
        if (this.onConnectionReadyCallback) {
          this.onConnectionReadyCallback();
        }
      }
    };
    
    this.ws.onmessage = (event) => {
      if (!this.canSendMessage) return;
      
      // Ignorar mensajes mientras se están limpiando las colas
      if (this.isClearingQueues) {
        return;
      }
      
      if (typeof event.data === 'string' && event.data !== 'Heartbeat received') {
        // Manejar errores LLM
        if (event.data === 'ErrorLLM01') {
          this.handleLLMError(true);
        } else if (event.data === 'ErrorLLM02') {
          this.handleLLMError(false);
        } else {
          // Detectar si es transcripción o respuesta del LLM
          if (event.data.includes('"role": "user", "content":') || 
              event.data.includes('"role":"user","content":') ||
              (event.data.includes('"role"') && event.data.includes('"user"') && event.data.includes('"content"'))) {
            
            // Solo procesar como transcripción si estábamos esperando una (audio enviado)
            if (this.expectingTranscription) {
              // Es la transcripción de un mensaje de audio
              try {
                const message = JSON.parse(event.data);
                // Detectar mensajes no válidos: "(non reconnu)" o similar
                const isNotRecognized = message.content && (
                  message.content.includes('(non reconnu)') ||
                  message.content.toLowerCase().includes('not recognized') ||
                  message.content.toLowerCase().includes('no reconocido')
                );
                
                if (message.content && !isNotRecognized) {
                  this.processTranscription(message.content);
                  this.expectingTranscription = false;
                } else {
                  // Mensaje no válido: activar flag y contar
                  this.lastUserMessageWasEmpty = true;
                  conversationSaverService.incrementNotValidCount(); // Contar mensaje no válido
                  this.expectingTranscription = false;
                }
              } catch (e) {
                // console.error(`[${this.sessionId}] [WebSocket] Error al parsear transcripción:`, e, 'Datos:', event.data);
                this.expectingTranscription = false;
              }
            }
          } else {
            // Es un chunk de respuesta del LLM
            this.processTextChunk(event.data);
          }
        }
      } else if (typeof event.data === 'object') {
        // Ignorar audio mientras se están limpiando las colas
        if (this.isClearingQueues) {
          return;
        }
        
        // Audio de respuesta - Encolar e intentar procesar par
        // Si la respuesta fue interrumpida, descartar
        if (this.isResponseInterrupted) {
          const audioBlob = new Blob([event.data], { type: 'audio/mp3' });
          this.interruptedAudioChunks.push(audioBlob);
          return;
        }
        
        const audioBlob = new Blob([event.data], { type: 'audio/mp3' });
        const audioURL = URL.createObjectURL(audioBlob);
        
        // Encolar audio
        this.audioQueue.push({ url: audioURL, blob: audioBlob });
        
        // Intentar procesar siguiente par si ambos están disponibles
        this.tryProcessNextChunkPair();
      }
    };
    
    this.ws.onclose = (event) => {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
      
      // Intentar reconectar si:
      // 1. No fue un cierre limpio (desconexión inesperada)
      // 2. O si fue un cierre limpio pero la página está visible (puede ser reconexión necesaria)
      const shouldReconnect = (!event.wasClean || (event.wasClean && !document.hidden)) && 
                              this.reconnectAttempts < this.maxReconnectAttempts;
      
      if (shouldReconnect) {
        this.reconnect();
      } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        // console.error(`[${this.sessionId}] [WebSocket] Máximo de intentos de reconexión alcanzado`);
      }
    };
    
    this.ws.onerror = (error) => {
      // console.error('[WebSocket] Error en conexión:', error);
      // console.error('[WebSocket] Estado del WebSocket:', this.ws?.readyState);
      // console.error('[WebSocket] URL intentada:', wsUrl.replace(/token=[^&]+/, 'token=***'));
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
      this.isConnecting = false;
    };
  }
  
  /**
   * Manejar errores LLM y reintentar
   */
  private handleLLMError(useBackup: boolean): void {
    const encodeBase64 = btoa('starting');
    // Construir contexto completo con system prompt + historial
    const fullContext = this.buildFullContext();
    
    const payload = JSON.stringify({
      context: fullContext,
      audio: encodeBase64,
      llm: useBackup ? this.backupLLM : this.mainLLM,
      model: useBackup ? this.backupModel : this.mainModel,
    });
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(payload);
    }
  }

  /**
   * Interrumpir la respuesta actual del asistente
   * - Detiene audio inmediatamente
   * - MANTIENE el texto visible en el chat
   * - Descarta chunks que sigan llegando
   */
  private interruptCurrentResponse(): void {
    // 1. Detener audio inmediatamente
    if (this.currentAudioElement) {
      this.currentAudioElement.pause();
      this.currentAudioElement = null;
    }
    this.isPlayingAudio = false;
    this.wasPlayingBeforeHidden = false;
    
    // 2. Activar flag de interrupción para descartar chunks siguientes
    this.isResponseInterrupted = true;
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    
    // 3. MANTENER el texto visible (marcar como completo aunque esté incompleto)
    const lastMessage = this.chathistory[this.chathistory.length - 1];
    if (lastMessage && lastMessage.role === 'assistant') {
      // Si no tiene timestamp, agregarlo para hacerlo visible
      if (!lastMessage.timestamp) {
        lastMessage.timestamp = Date.now();
      }
    }
    
    // 4. Desactivar avatar
    if (this.onAvatarStateCallback) {
      this.onAvatarStateCallback(false);
    }
    
    // 5. Limpiar AMBAS colas (texto y audio pendientes)
    if (this.messageQueue.length > 0 || this.audioQueue.length > 0) {
      this.messageQueue = [];
      this.audioQueue.forEach(item => URL.revokeObjectURL(item.url));
      this.audioQueue = [];
    }
  }

  /**
   * Limpiar completamente una respuesta interrumpida
   * FASE 2: Se ejecuta cuando detectamos que la respuesta interrumpida terminó
   */
  private cleanupInterruptedResponse(): void {
    // 1. Limpiar cola de audio pendiente
    if (this.audioQueue.length > 0) {
      this.audioQueue.forEach(item => URL.revokeObjectURL(item.url));
      this.audioQueue = [];
    }
    
    // 2. Limpiar chunks de texto acumulados
    if (this.accumulatedChunks.length > 0) {
      this.accumulatedChunks = '';
    }
    
    // 3. Descartar chunks interrumpidos acumulados
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    
    // 4. Eliminar el último mensaje del asistente si está incompleto
    const lastMessage = this.chathistory[this.chathistory.length - 1];
    if (lastMessage && lastMessage.role === 'assistant') {
      this.chathistory.pop();
      
      // Notificar callback para actualizar la UI
      if (this.onMessageCallback && this.chathistory.length > 0) {
        const newLastMessage = this.chathistory[this.chathistory.length - 1];
        this.onMessageCallback(newLastMessage);
      }
    }
    
    // 5. Resetear flags
    this.isResponseInterrupted = false;
  }

  /**
   * Enviar mensaje de audio
   */
  async sendAudioMessage(audioBlob: Blob, inputType: string = 'audio'): Promise<void> {
    try {
      // Convertir audio a base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Audio = (reader.result as string).split(',')[1];
        this.sendMessage(base64Audio, inputType);
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error('Error al enviar mensaje de audio:', error);
      throw error;
    }
  }

  /**
   * Enviar mensaje de texto
   */
  async sendTextMessage(text: string): Promise<void> {const trimmedText = text.trim();
    if (!trimmedText || trimmedText.length === 0) {
      // Mensaje vacío: activar flag pero no agregar al historial
      // Aún así enviar al WebSocket para que el asistente responda
      this.lastUserMessageWasEmpty = true;
      conversationSaverService.incrementNotValidCount(); // Contar mensaje no válidothis.sendMessage('', 'text');
      return;
    }
    this.lastUserMessageWasEmpty = false;this.sendMessage(trimmedText, 'text');
  }

  private sendMessage(content: string, inputType: string): void {
      // INTERRUPCIÓN: Cuando el usuario envía un nuevo mensaje, interrumpir la respuesta actual
      this.interruptCurrentResponse();
      
      // Resetear flag de interrupción ANTES de enviar el nuevo mensaje
      // para que la nueva respuesta no se descarte
      this.isResponseInterrupted = false;
      
      // Resetear accumulatedChunks para la nueva respuesta
      // Esto previene que chunks de respuestas anteriores se acumulen
      this.accumulatedChunks = '';
      
      if (inputType === 'audio') {
      // El audio base64 NO se guarda en chathistory
      // Solo se envía al WebSocket para procesamiento con Gemini
      // El texto transcrito llegará después vía processTranscription
      this.expectingTranscription = true; // Marcar que esperamos transcripción
      
      // Construir contexto completo con system prompt + historial
      const fullContext = this.buildFullContext();
      
      const payload = JSON.stringify({
        context: fullContext,
        entry_format: 'audio',
        audio: content,
        model: this.mainModel,
        llm: this.mainLLM,
        llm_transcription_model: this.transcriptionModel,
      });
      
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(payload);
        // NO registrar interacción aquí - se cuenta por conversación, no por mensaje
      } else {
        // console.warn(`[${this.sessionId}] [Mensaje Audio] WebSocket no está listo (estado: ${this.ws?.readyState}). Encolando mensaje...`);
        this.pendingSendQueue.push({ payload, type: 'audio' });
      }
    } else {
      // Los mensajes de texto se guardan en chathistory solo si no están vacíos
      // Si el flag lastUserMessageWasEmpty está activo, no agregar al historial
      if (!this.lastUserMessageWasEmpty && content && content.trim().length > 0) {
        const message: ChatMessage = {
          role: 'user',
          content: '[text] ' + content,
          timestamp: Date.now(),
        };

        this.chathistory.push(message);
        
        // Detener contador de tiempo muerto cuando el usuario habla
        conversationSaverService.stopIdleTimer();
        
      // Notificar callback inmediatamente para actualizar la UI
      if (this.onMessageCallback) {
        this.onMessageCallback(message);
      }

      // Evaluar mensaje para objetivos académicos si está activo
      if (this.academicMode.isActive) {this.evaluateUserMessageForObjectives(content).catch((err) => { /* console.error(err); */ });
      }
      }
      
      // Construir contexto completo con system prompt + historial
      const fullContext = this.buildFullContext();
      
      const payload = JSON.stringify({
        context: fullContext,
        entry_format: 'text',
        text: content,
        audio: '',
        model: this.mainModel,
        llm: this.mainLLM,
        llm_transcription_model: this.transcriptionModel,
      });
      
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {this.ws.send(payload);
        // NO registrar interacción aquí - se cuenta por conversación, no por mensaje
      } else {
        // console.warn(`[${this.sessionId}] [Mensaje Texto] WebSocket no está listo (estado: ${this.ws?.readyState}). Encolando mensaje...`);
        this.pendingSendQueue.push({ payload, type: 'text' });
      }
    }
  }

  /**
   * Activar modo académico (solo guarda el estado, no cambia prompt reference)
   */
  setAcademicMode(
    level: string,
    unit: number,
    lesson: number,
    objectives: LessonObjective[],
    userEmail: string,
    onObjectiveUpdate?: (objectiveDescription: string, isCorrect: boolean) => void,
    personalizedTask?: string,
    onEvaluationComplete?: () => void
  ): void {
    this.academicMode = {
      isActive: true,
      level,
      unit,
      lesson,
      objectives,
      personalizedTask,
      userEmail,
      onObjectiveUpdate,
      onEvaluationComplete,
    };
  }

  /**
   * Iniciar actividad de lección: cambiar prompt reference a ruta académica y reconectar WebSocket.
   * Galia conversacional solo se usa en la inicialización normal de la app.
   */
  async startLessonActivity(): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.objectives || !this.academicMode.userEmail) {
      // console.warn('[WebSocket] No se puede iniciar actividad: modo académico no activo o incompleto');
      return;
    }

    // 1. Guardar conversación actual en Lambda antes de cambiar el prompt reference
    // Seguir la misma lógica que cuando se cambia la configuración en el menú
    const currentMessages = this.getChatHistory();
    
    if (currentMessages.length > 0) {
      const finalMessages = currentMessages.map((m) => ({
        role: m.role,
        content: m.content,
        afterInvalidMessage: m.afterInvalidMessage,
      }));
      
      try {
        await conversationSaverService.guardarData(finalMessages);
      } catch (error) {
        // console.error('[WebSocket] ❌ Error al guardar conversación anterior:', error);
        // Continuar aunque haya error al guardar
      }
    }

    // 2. Limpiar todas las colas de audio y mensajes pendientes
    this.clearAllQueues();

    // 3. Limpiar historial de chat y resetear estado
    this.clearChatHistory(); // Usar clearChatHistory() para que se actualice la UI inmediatamente
    this.lastUserMessageWasEmpty = false;
    this.accumulatedChunks = '';
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    this.isResponseInterrupted = false;
    this.expectingTranscription = false;
    this.sessionId = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // 4. Resetear contadores en conversationSaverService
    conversationSaverService.resetConversationState();
    
    // 5. Cambiar el prompt reference a la ruta académica (lección/actividad)
    const config = configService.getConfig();
    const isTestingPhase = config.testingPhase;
    const suffix = isTestingPhase ? '-dev' : '-pro';
    const academicRoutePromptReference = `alianzafrancesa-galia-ruta-academica${suffix}`;
    configService.setPromptReference(academicRoutePromptReference);

    // 6. Desconectar WebSocket actual si está conectado para forzar reconexión con nuevo prompt reference
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      this.disconnect();
      
      // Esperar un momento para que la desconexión se complete
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // 7. Reconectar con el contexto académico aplicado
    try {
      await this.initialize();
    } catch (error) {
      throw error;
    }
  }


  /**
   * Desactivar modo académico
   */
  clearAcademicMode(): void {
    this.academicMode = { isActive: false };
  }

  /**
   * Obtener estado actual del modo académico
   */
  getAcademicMode(): { isActive: boolean; level?: string; unit?: number; lesson?: number } {
    return {
      isActive: this.academicMode.isActive,
      level: this.academicMode.level,
      unit: this.academicMode.unit,
      lesson: this.academicMode.lesson
    };
  }

  /**
   * Evaluar mensaje del usuario para detectar cumplimiento de objetivos
   * Usa GPT para evaluar si la frase actual cumple con los objetivos
   */
  private async evaluateUserMessageForObjectives(userMessage: string): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.objectives || !this.academicMode.userEmail) {
      return;
    }

    const { level, unit, lesson, objectives, userEmail, onObjectiveUpdate } = this.academicMode;

    // Obtener progreso para pasar frases usadas a GPT (GPT decidirá si descartar repeticiones)
    const progress = await academicService.getUserLessonProgress(userEmail, level!, unit!, lesson!);
    const allUsedPhrases: string[] = [];
    
    if (progress) {
      // Recopilar todas las frases usadas de todos los objetivos
      allUsedPhrases.push(...Object.values(progress.objectives_status)
        .flatMap(status => status.used_phrases || []));
    }

    // Evaluar objetivos usando LLM, pasando la lista de frases usadas
    // LLM decidirá si la frase es repetida y descartará objetivos en consecuencia
    const evaluationResults = await objectiveEvaluationService.evaluateObjectivesWithGPT(
      userMessage,
      objectives
    );

    // Si no hay resultados (error en GPT), usar evaluación básica como fallback
    if (evaluationResults.length === 0) {
      return;
    }

    // El servicio de evaluación ya devuelve los objetivos ordenados por prioridad
    // (objetivos cumplidos con mayor priority_score primero)
    // Simplemente tomamos el primer objetivo cumplido con confianza suficiente
    const topPriorityResult = evaluationResults.find(
      (result: ObjectiveEvaluationResult) => result.is_fulfilled && result.confidence >= 0.5
    );

    // Si no hay objetivos cumplidos, no procesar nada
    if (!topPriorityResult) {
      return;
    }

    // Encontrar el objetivo correspondiente usando la descripción
    const objective = objectives.find(obj => obj.description === topPriorityResult.description);
    if (!objective) {
      return;
    }

    const isCorrect = topPriorityResult.is_fulfilled;
    const confidence = topPriorityResult.confidence;

    // Procesar el objetivo con mayor prioridad (ya viene priorizado del servicio de evaluación)
    if (isCorrect && confidence >= 0.5) {
      // Para objetivos de vocabulario, el servicio de evaluación puede incluir palabras encontradas
      // en el futuro, pero por ahora simplemente pasamos undefined y el servicio académico
      // puede extraerlas dinámicamente si es necesario
      let vocabularyWordsFound: string[] | undefined = undefined;

      // Actualizar progreso del objetivo
      if (onObjectiveUpdate) {
        onObjectiveUpdate(objective.description, isCorrect);
      }

      // Actualizar en base de datos
      console.log('[WebSocketService] 🔄 Actualizando estado de objetivo:', {
        userEmail,
        level: level!,
        unit: unit!,
        lesson: lesson!,
        objectiveDescription: objective.description,
        isCorrect,
        academicModeLevel: this.academicMode.level,
        academicModeUnit: this.academicMode.unit,
        academicModeLesson: this.academicMode.lesson
      });
      
      const updatedProgress = await academicService.updateObjectiveStatus(
        userEmail,
        level!,
        unit!,
        lesson!,
        objective.description,
        isCorrect,
        vocabularyWordsFound,
        userMessage // Pasar el mensaje para verificar repetición
      );

      // Actualizar el prompt con el progreso actualizado de objetivos para que la IA sepa qué objetivos ya se cumplieron
      await this.updatePromptWithCurrentObjectives();

      // Disparar evaluación cuando todos los objetivos están completos.
      // Usar el progreso recién actualizado evita condiciones de carrera y asegura que
      // en tareas personalizadas (ACTIVIDAD_xxx) la evaluación se ejecute correctamente.
      if (updatedProgress?.all_objectives_completed) {
        await this.evaluateWithRubric();
        return;
      }
    }

    // Fallback: verificar si todos los objetivos están completados (p. ej. si updateObjectiveStatus devolvió null)
    const allCompleted = await academicService.checkAllObjectivesCompleted(
      userEmail,
      level!,
      unit!,
      lesson!
    );

    if (allCompleted) {
      await this.evaluateWithRubric();
    }
  }

  /**
   * Evaluar conversación completa con rúbrica cuando se completan todos los objetivos
   */
  private async evaluateWithRubric(): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.userEmail) {
      return;
    }

    const { level, unit, lesson, userEmail } = this.academicMode;

    try {
      // Obtener progreso actual
      const progress = await academicService.getUserLessonProgress(
        userEmail,
        level!,
        unit!,
        lesson!
      );

      if (!progress || !progress.all_objectives_completed) {
        return;
      }

      // Convertir chathistory a formato de conversación
      const conversation = this.chathistory.map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
      }));

      // Obtener tarea y objetivos del modo académico o del progreso
      // Prioridad: 1) academicMode (sesión actual), 2) progress (guardado)
      const taskDescription = this.academicMode.personalizedTask || progress.personalized_task;
      
      // Obtener objetivos: primero desde academicMode, luego desde objectives_status
      let objectives: Array<{ description: string }> | undefined;
      if (this.academicMode.objectives && this.academicMode.objectives.length > 0) {
        objectives = this.academicMode.objectives.map(obj => ({ description: obj.description }));
      } else if (progress.objectives_status) {
        objectives = Object.keys(progress.objectives_status).map(desc => ({ description: desc }));
      }

      // Evaluar con rúbrica
      const evaluation = await academicEvaluationService.evaluateWithRubric(
        conversation,
        level!,
        unit!,
        lesson!,
        progress.objectives_status,
        taskDescription,
        objectives
      );

      if (evaluation) {
        // Guardar evaluación
        await academicEvaluationService.saveEvaluation(
          userEmail,
          level!,
          unit!,
          lesson!,
          evaluation
        );

        // Notificar que la evaluación se completó para actualizar la interfaz
        const hasCallback = !!this.academicMode.onEvaluationComplete;
        if (this.academicMode.onEvaluationComplete) {
          this.academicMode.onEvaluationComplete();
        } else {
          // console.warn('[WebSocket] ⚠️ No hay callback onEvaluationComplete registrado');
        }
      } else {
        // console.warn('[WebSocket] ⚠️ No se generó evaluación');
      }
    } catch (error) {
      // Error al evaluar con rúbrica
    }
  }

  /**
   * Disparar la evaluación con rúbrica desde la UI cuando todos los objetivos están completos.
   * Útil cuando el flujo interno (evaluateUserMessageForObjectives) no pudo ejecutarla (p. ej. updateObjectiveStatus devolvió null por 406/409).
   */
  async triggerEvaluationIfAllComplete(): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.userEmail) {
      return;
    }
    await this.evaluateWithRubric();
  }

  /**
   * Evaluar la calidad pedagógica de la respuesta del asistente
   * Se ejecuta automáticamente cuando el asistente termina de responder
   */
  private async evaluateAssistantResponse(assistantMessage: string): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.objectives) {
      return;
    }

    // Obtener el último mensaje del usuario para contexto
    const userMessages = this.chathistory.filter(m => m.role === 'user');
    if (userMessages.length === 0) {
      return; // No hay mensaje del usuario para evaluar
    }

    const lastUserMessage = userMessages[userMessages.length - 1].content;
    
    try {
      const evaluation = await assistantResponseEvaluationService.evaluateAssistantResponse(
        assistantMessage,
        lastUserMessage,
        this.academicMode.objectives
      );

      if (!evaluation) {
        return; // Error en la evaluación
      }

      // Aquí se podría guardar la evaluación en Supabase para analytics
      // o enviar a un endpoint para revisión por desarrolladores
      // Por ahora solo se loguea

    } catch (error) {
      // Error al evaluar respuesta del asistente
    }
  }

  /**
   * Actualizar el prompt con el progreso actualizado de objetivos
   * Se llama cuando se cumple un objetivo para que la IA sepa en tiempo real qué objetivos ya se completaron
   */
  private async updatePromptWithCurrentObjectives(): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.userEmail || !this.academicMode.personalizedTask) {
      return;
    }

    const { level, unit, lesson, userEmail } = this.academicMode;

    try {
      // Obtener progreso actualizado
      const progress = await academicService.getUserLessonProgress(
        userEmail,
        level!,
        unit!,
        lesson!
      );

      if (!progress || !progress.objectives_status) {
        return;
      }

      // Reconstruir prompt localmente con progreso actualizado
      const config = configService.getConfig();
      
      const objectivesProgress = Object.entries(progress.objectives_status).map(([description, status]: [string, any]) => ({
        description,
        completed: status.completed || false,
        correct_count: status.correct_count || 0,
        total_correct: status.total_correct || 0,
      }));

      // Obtener datos dinámicos para re-ensamblar
      const apolearnData = authService.getApolearnUserData();
      const dynamicCtx: import('../config/prompts').DynamicPromptContext = {
        userFullName: config.userFullName || '',
        userCity: (apolearnData?.user as any)?.city || '',
        userRole: apolearnData?.role || '',
        colombiaTime: new Date(),
        eventosAF: '',
        summaryPrevAssistant: '',
      };

      const pType = (config.personalityType || 'Amigable') as import('../types').PersonalityType;
      const fType = (config.functionalityType || 'Conversadora') as import('../types').FunctionalityType;
      const mLevel = (config.mcerLevel || 'B1') as import('../types').MCERLevel;

      let fullPrompt = assembleFullPrompt(dynamicCtx, pType, fType, mLevel);

      // Construir el texto de estado de objetivos actualizado
      let objectivesStatusText = '';
      const completedObjectives = objectivesProgress
        .filter((obj: any) => obj.completed)
        .map((obj: any) => obj.description);
      const pendingObjectives = objectivesProgress
        .filter((obj: any) => !obj.completed)
        .map((obj: any) => obj.description);
      
      if (completedObjectives.length > 0) {
        objectivesStatusText += `\n\nOBJETIVOS YA CUMPLIDOS (no te enfoques en estos, ya fueron completados):\n${completedObjectives.map((desc: string, idx: number) => `${idx + 1}. ${desc}`).join('\n')}`;
      }
      
      if (pendingObjectives.length > 0) {
        objectivesStatusText += `\n\nOBJETIVOS PENDIENTES (enfócate en estos durante la conversación):\n${pendingObjectives.map((desc: string, idx: number) => `${idx + 1}. ${desc}`).join('\n')}`;
      }

      // Reconstruir las instrucciones de tarea con el estado actualizado
      const taskInstructions = `

---

## CONTEXTO DE LA ACTIVIDAD ACADÉMICA

### TAREA:
El estudiante debe realizar la siguiente tarea:
${this.academicMode.personalizedTask}

### OBJETIVOS (Lo que debe cumplir):
Los objetivos son los criterios específicos que el estudiante debe demostrar durante la conversación. ${objectivesStatusText ? `\n${objectivesStatusText}` : ''}

**IMPORTANTE - Diferencia entre TAREA y OBJETIVOS:**
- **TAREA**: Es el logro que el estudiante debe cumplir. Hace parte del contexto general de la conversación.
- **OBJETIVOS**: Son los criterios específicos que el estudiante debe cumplir durante la conversación. El estudiante completa la tarea cuando cumple TODOS los objetivos.

**COMPLETACIÓN DE LA ACTIVIDAD:**
La actividad se considera completada cuando el estudiante cumple TODOS los objetivos durante la ejecución de la tarea. Una vez que todos los objetivos estén cumplidos, la tarea se dará por completada.

INSTRUCCIONES CRÍTICAS:
1. Si la tarea no lo especifica, inicia con proponerle al estudiante una forma dinámica de cumplir con la tarea para cumplir con sus objetivos (puede ser un juego de rol, una situación, etc.)
2. Si se trata de un juego de rol, ten en cuenta: El juego de rol debe ser natural y únicamente debes hablar en francés mientras estén en el juego de rol. No des explicaciones, solo fluye en la converación sin hacer correcciones durante el juego de rol. Si han iniciado un juego de rol, el usuario se encargará de decirte cuando detenerse. Si el usuario hace preguntas en medio del juego de rol, busca la manera de resolverla sin perder tu personaje. No coloques viñetas, ni parentesis ni indiques que es tu turno de hablar; ni indicaciones ni parentesis.
3. ENFÓCATE PRINCIPALMENTE EN LOS OBJETIVOS PENDIENTES. Si un objetivo ya fue cumplido, no necesitas volver a trabajarlo a menos que el estudiante lo mencione específicamente.

Recuerda: La tarea es el contexto/situación, pero los objetivos son lo que realmente debe cumplir el estudiante. La actividad se completa cuando todos los objetivos están cumplidos.`;

      // Actualizar el prompt completo con la información actualizada
      const updatedPrompt = fullPrompt + taskInstructions;
      configService.setFullPrompt(updatedPrompt);
      this.currentPrompt = updatedPrompt;

      console.log('[WebSocketService] ✅ Prompt actualizado con progreso de objetivos:', {
        completed: completedObjectives.length,
        pending: pendingObjectives.length
      });
    } catch (error) {
      console.error('[WebSocketService] Error al actualizar prompt con objetivos:', error);
    }
  }

  /**
   * Procesar transcripción de audio recibida del WebSocket
   * El WebSocket procesa el audio con Gemini y devuelve el texto transcrito
   */
  processTranscription(transcribedText: string): void {
    // IMPORTANTE: Si había una respuesta interrumpida, este es el momento de limpiarla completamente
    // La transcripción indica que el usuario ya envió su nuevo mensaje, por lo que la respuesta
    // anterior ya debería haber terminado de llegar (o está a punto de terminar)
    if (this.isResponseInterrupted) {
      this.cleanupInterruptedResponse();
    }

    // Procesar el texto transcrito como mensaje del usuario
    // IMPORTANTE: Si había una respuesta interrumpida, este es el momento de limpiarla completamente
    // La transcripción indica que el usuario ya envió su nuevo mensaje, por lo que la respuesta
    // anterior ya debería haber terminado de llegar (o está a punto de terminar)
    if (this.isResponseInterrupted) {
      this.cleanupInterruptedResponse();
    }
    
    const trimmedText = transcribedText.trim();
    if (!trimmedText || trimmedText.length === 0) {
      // Transcripción vacía: activar flag pero no agregar al historial
      this.lastUserMessageWasEmpty = true;
      conversationSaverService.incrementNotValidCount(); // Contar mensaje no válido
      this.expectingTranscription = false;
      return;
    }
    
    // Cuando llega la transcripción de Gemini, agregarla al chathistory
    const message: ChatMessage = {
      role: 'user',
      content: '[audio] ' + trimmedText,
      timestamp: Date.now(),
    };

    this.chathistory.push(message);
    this.lastUserMessageWasEmpty = false; // Resetear flag ya que el mensaje no está vacío

    // Detener contador de tiempo muerto cuando el usuario habla (transcripción recibida)
    conversationSaverService.stopIdleTimer();

    // NO registrar interacción aquí - se cuenta por conversación, no por mensaje

    // Notificar al callback para actualizar la UI
    if (this.onMessageCallback) {
      this.onMessageCallback(message);
    }

    // Evaluar mensaje para objetivos académicos si está activo
    if (this.academicMode.isActive) {
      this.evaluateUserMessageForObjectives(trimmedText).catch((err) => { /* console.error(err); */ });
    }
    
    this.expectingTranscription = false;
  }

  /**
   * Procesar respuesta del WebSocket (chunk de texto)
   * Encola el chunk para procesamiento 1:1 con su audio correspondiente
   */
  processTextChunk(chunk: string): void {
    // Ignorar chunks mientras se están limpiando las colas
    if (this.isClearingQueues) {
      return;
    }

    // Si la respuesta fue interrumpida, descartar
    if (this.isResponseInterrupted) {
      this.interruptedResponseChunks.push(chunk);
      return;
    }

    if (!chunk || chunk.trim() === '') {
      return;
    }
    
    // ENCOLAR el chunk individual para procesamiento chunk por chunk
    // NO acumular aquí - se acumulará en tryProcessNextChunkPair después de limpiar
    // Esto evita duplicación ya que el chunk se procesa una vez en tryProcessNextChunkPair
    this.messageQueue.push(chunk);
    
    // Intentar procesar siguiente par si ambos están disponibles
    this.tryProcessNextChunkPair();
  }

  /**
   * Intenta procesar el siguiente par (texto + audio) si ambos están disponibles
   * Similar al Update() de Unity que verifica audioClipList.Count > 0 && !audioSource.isPlaying
   * Crea una NUEVA burbuja por cada chunk (modelo Unity)
   */
  private tryProcessNextChunkPair(): void {
    // Solo procesar si:
    // 1. NO está reproduciendo audio actualmente
    // 2. Hay texto Y audio disponibles en sus colas
    if (this.isPlayingAudio) {
      return;
    }
    
    if (this.messageQueue.length === 0 || this.audioQueue.length === 0) {
      return;
    }
    
    // Dequeue un par: texto + audio
    const textChunk = this.messageQueue.shift()!;
    const audioItem = this.audioQueue.shift()!;
    
    // Limpiar espacios en blanco antes y después
    const cleanedText = textChunk.trim();
    
    // Si el texto está vacío después del trim, no mostrar pero sí reproducir audio
    if (!cleanedText || cleanedText.length === 0) {
      this.playAudioChunk(audioItem);
      return;
    }
    
    // 1. CREAR NUEVO MENSAJE con solo este chunk limpio (como Unity: AppendMessage)
    // Guardar audioUrl para poder reproducir después al hacer click en la burbuja
    // Almacenar el blob para no perder la referencia cuando se revoque la URL
    this.audioBlobStore.set(audioItem.url, audioItem.blob);
    
    // Si el último mensaje del usuario fue vacío, este es el primer mensaje del asistente después
    // Marcar este mensaje para que no se concatene con el anterior
    const wasEmptyMessage = this.lastUserMessageWasEmpty;
    if (wasEmptyMessage) {
      this.lastUserMessageWasEmpty = false;
    }
    
    const message: ChatMessage = {
      role: 'assistant',
      content: cleanedText,  // ✅ Texto limpio sin espacios al inicio/fin
      timestamp: Date.now(),
      audioUrl: audioItem.url, // Guardar URL del audio para reproducir después
      afterInvalidMessage: wasEmptyMessage, // Marcar si viene después de un mensaje no válido
    };
    
    this.chathistory.push(message);
    
    // 2. Notificar UI para mostrar la nueva burbuja
    if (this.onMessageCallback) {
      this.onMessageCallback(message);
    }
    
    // 3. Acumular para consolidación final (con un espacio entre chunks)
    if (this.accumulatedChunks.length > 0) {
      this.accumulatedChunks += ' ';
    }
    this.accumulatedChunks += cleanedText;
    
    // 4. REPRODUCIR audio del chunk
    this.playAudioChunk(audioItem);
  }

  /**
   * Reproduce un chunk de audio
   * Cuando termina, intenta procesar el siguiente par automáticamente
   */
  private playAudioChunk(audioItem: { url: string; blob: Blob }): void {
    // Si hay audio manual reproduciéndose, saltar este chunk automático
    // El audio manual tiene prioridad
    if (this.isManualAudioPlaying) {
      // Saltar este chunk - no reproducir
      this.isPlayingAudio = false;
      // Si hay más pares en cola, intentar procesar el siguiente después
      // Pero solo si el audio manual termina
      return;
    }
    
    this.isPlayingAudio = true;
    
    // Activar avatar
    if (this.onAvatarStateCallback) {
      this.onAvatarStateCallback(true);
    }
    
    const audio = new Audio(audioItem.url);
    this.currentAudioElement = audio;
    
    audio.onended = () => {
      // NO revocar la URL aquí, mantenerla para reproducir después al hacer click
      // URL.revokeObjectURL(audioItem.url); // Comentado para permitir reproducción posterior
      this.isPlayingAudio = false;
      this.currentAudioElement = null;
      
      // Si no hay más pares, finalizar respuesta
      if (this.messageQueue.length === 0 && this.audioQueue.length === 0) {
        this.finishResponse();
      } else {
        // Intentar procesar siguiente par
        this.tryProcessNextChunkPair();
      }
    };
    
    audio.onerror = (error) => {
      console.error(`[${this.sessionId}] [Audio] Error al reproducir chunk:`, error);
      // Limpiar del store si hay error
      this.audioBlobStore.delete(audioItem.url);
      this.isPlayingAudio = false;
      this.currentAudioElement = null;
      
      // Intentar siguiente o finalizar
      if (this.messageQueue.length > 0 && this.audioQueue.length > 0) {
        this.tryProcessNextChunkPair();
      } else {
        this.finishResponse();
      }
    };
    
    audio.play().catch((error) => {
      // console.error(`[${this.sessionId}] [Audio] Error al iniciar chunk:`, error);
      this.isPlayingAudio = false;
      this.currentAudioElement = null;
      if (this.messageQueue.length > 0 && this.audioQueue.length > 0) {
        this.tryProcessNextChunkPair();
      } else {
        this.finishResponse();
      }
    });
  }


  /**
   * Finalizar respuesta (cuando termina el audio)
   * NO consolida - mantiene los chunks como burbujas separadas
   * Esto permite que cada burbuja tenga su propio audio reproducible
   */
  finishResponse(): void {
    // NO consolidar - mantener los chunks individuales como burbujas separadas
    // Esto permite:
    // 1. Ver cada chunk como burbuja separada (mejor UX)
    // 2. Reproducir el audio de cada burbuja individualmente
    
    // Evaluar la calidad de la respuesta del asistente si hay modo académico activo
    if (this.academicMode.isActive && this.accumulatedChunks.trim().length > 0) {
      this.evaluateAssistantResponse(this.accumulatedChunks.trim()).catch(() => {});
    }
    
    // Limpiar acumulador
    this.accumulatedChunks = '';

    // NO registrar interacción aquí - se cuenta por conversación, no por mensaje

    // Iniciar contador de tiempo muerto cuando el asistente termina de responder
    conversationSaverService.startIdleTimer();

    // Desactivar animación del avatar
    if (this.onAvatarStateCallback) {
      this.onAvatarStateCallback(false);
    }

    // Limpiar cola de audio
    this.audioQueue.forEach(item => URL.revokeObjectURL(item.url));
    this.audioQueue = [];
  }

  /**
   * Obtener el historial de chat
   */
  getChatHistory(): ChatMessage[] {
    return [...this.chathistory];
  }

  /**
   * Agregar mensaje del sistema al historial de chat
   * Útil para mensajes de bienvenida o información del sistema
   */
  addSystemMessage(content: string): void {
    const systemMessage: ChatMessage = {
      role: 'assistant',
      content: content,
      timestamp: Date.now(),
    };
    this.chathistory.push(systemMessage);
    
    // Notificar al callback si existe
    if (this.onMessageCallback) {
      this.onMessageCallback(systemMessage);
    }
  }

  /**
   * Obtener URL de audio para un mensaje (recrear si es necesario)
   * Útil para reproducir audio al hacer click en burbujas
   */
  getAudioUrlForMessage(audioUrl: string): string | null {
    // Si el blob está en el store, recrear la URL si es necesario
    const blob = this.audioBlobStore.get(audioUrl);
    if (blob) {
      // La URL original debería seguir funcionando si no se revocó
      // Si no funciona, recrear desde el blob
      return audioUrl;
    }
    return null;
  }

  /**
   * Recrear URL de audio desde blob almacenado
   */
  recreateAudioUrl(audioUrl: string): string | null {
    const blob = this.audioBlobStore.get(audioUrl);
    if (blob) {
      return URL.createObjectURL(blob);
    }
    return null;
  }

  /**
   * Cargar historial de chat
   */
  loadChatHistory(messages: ChatMessage[]): void {
    this.chathistory = [...messages];
  }

  /**
   * Limpiar historial de chat
   */
  clearChatHistory(): void {
    this.chathistory = [];
    // Notificar que el historial fue limpiado
    if (this.onChatHistoryClearedCallback) {
      this.onChatHistoryClearedCallback();
    }
  }

  /**
   * Registrar callback para cuando se limpia el historial
   */
  onChatHistoryCleared(callback: () => void): void {
    this.onChatHistoryClearedCallback = callback;
  }

  /**
   * Desregistrar callback de historial limpiado
   */
  offChatHistoryCleared(): void {
    this.onChatHistoryClearedCallback = undefined;
  }

  /**
   * Enviar mensaje inicial con el system prompt
   * Se debe llamar después de que el WebSocket se conecte y el usuario presione "Aceptar"
   */
  sendInitialMessage(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!this.currentPrompt || this.currentPrompt.trim() === '') {
      // console.warn('[WebSocket] No se puede enviar mensaje inicial - prompt no disponible');
      return;
    }
    
    // Enviar mensaje inicial con el system prompt
    const encodeBase64 = btoa('starting');
    const systemContext = [{ role: 'system', content: this.currentPrompt }];
    const payload = JSON.stringify({
      context: JSON.stringify(systemContext),
      entry_format: 'audio',
      audio: encodeBase64,
      model: this.mainModel,
      llm: this.mainLLM,
      llm_transcription_model: this.transcriptionModel,
    });
    
    try {
      this.ws.send(payload);
    } catch (error) {
      // console.error('[WebSocket] ❌ Error al enviar mensaje inicial:', error);
    }
  }

  /**
   * Limpiar todas las colas de audio y mensajes pendientes
   */
  clearAllQueues(): void {
    // Activar flag para ignorar mensajes entrantes durante la limpieza
    this.isClearingQueues = true;
    
    // Detener audio actual
    this.stopAutomaticAudio();
    
    // Limpiar colas de mensajes y audio
    this.messageQueue.forEach(() => {});
    this.messageQueue = [];
    
    this.audioQueue.forEach(item => URL.revokeObjectURL(item.url));
    this.audioQueue = [];
    
    // Limpiar acumuladores
    this.accumulatedChunks = '';
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    
    // Desactivar flag después de un breve delay para permitir que cualquier mensaje en tránsito se descarte
    setTimeout(() => {
      this.isClearingQueues = false;
    }, 100);
  }

  /**
   * Callbacks
   */
  onMessage(callback: (message: ChatMessage) => void): void {
    this.onMessageCallback = callback;
  }

  offMessage(): void {
    this.onMessageCallback = undefined;
  }

  /**
   * Registrar callback para cuando la conexión esté completamente lista
   */
  onConnectionReady(callback: () => void): void {
    this.onConnectionReadyCallback = callback;
    // Si ya está conectado, llamar inmediatamente
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      callback();
    }
  }

  /**
   * Desregistrar callback de conexión lista
   */
  offConnectionReady(): void {
    this.onConnectionReadyCallback = undefined;
  }

  /**
   * Verificar si el WebSocket está completamente conectado
   */
  isWebSocketReady(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  onAudio(callback: (audioUrl: string) => void): void {
    this.onAudioCallback = callback;
  }

  offAudio(): void {
    this.onAudioCallback = undefined;
  }

  onAvatarState(callback: (isHablando: boolean) => void): void {
    this.onAvatarStateCallback = callback;
  }

  offAvatarState(): void {
    this.onAvatarStateCallback = undefined;
  }

  /**
   * Reconectar si se pierde la conexión
   */
  private reconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Máximo de intentos de reconexión alcanzado');
      return;
    }

    // Verificar que el prompt esté disponible antes de reconectar
    const config = configService.getConfig();
    if (!config.fullPrompt || config.fullPrompt.trim() === '') {
      // console.error('[WebSocket] No se puede reconectar: prompt no disponible');
      this.isConnecting = false;
      return;
    }

    this.reconnectAttempts++;
    setTimeout(() => {
      this.initialize();
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  /**
   * Detener reproducción automática de audio
   * Útil cuando el usuario quiere reproducir un audio manualmente
   */
  stopAutomaticAudio(): void {
    if (this.currentAudioElement) {
      this.currentAudioElement.pause();
      this.currentAudioElement = null;
    }
    this.isPlayingAudio = false;
    // Desactivar avatar si estaba hablando
    if (this.onAvatarStateCallback) {
      this.onAvatarStateCallback(false);
    }
  }

  /**
   * Notificar al servicio si hay audio manual reproduciéndose
   */
  setManualAudioPlaying(isPlaying: boolean): void {
    this.isManualAudioPlaying = isPlaying;
    // Si el audio manual terminó y hay chunks en cola, intentar procesar el siguiente
    if (!isPlaying && this.messageQueue.length > 0 && this.audioQueue.length > 0) {
      // Intentar procesar el siguiente par después de un breve delay
      setTimeout(() => {
        if (!this.isManualAudioPlaying && !this.isPlayingAudio) {
          this.tryProcessNextChunkPair();
        }
      }, 100);
    }
  }

  /**
   * Verificar si audio manual está reproduciéndose
   */
  isManualAudioActive(): boolean {
    return this.isManualAudioPlaying;
  }

  /**
   * Cerrar conexión
   */
  disconnect(): void {
    
    // Remover listener de visibilidad
    if (this.visibilityChangeHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
    
    // Limpiar audio en reproducción
    if (this.currentAudioElement) {
      this.currentAudioElement.pause();
      this.currentAudioElement = null;
    }
    this.isPlayingAudio = false;
    this.wasPlayingBeforeHidden = false;
    this.isManualAudioPlaying = false;
    
    // Limpiar colas de mensajes y audio
    this.messageQueue = [];
    this.audioQueue.forEach(item => URL.revokeObjectURL(item.url));
    this.audioQueue = [];
    this.accumulatedChunks = '';
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    this.pendingSendQueue = [];
    
    // No limpiar chathistory aquí: al cerrar/abrir el panel el usuario debe seguir viendo su conversación.
    // El historial solo se limpia con clearChatHistory() o al cargar uno nuevo con loadChatHistory().

    // Resetear flags
    this.isResponseInterrupted = false;
    this.expectingTranscription = false;
    this.lastUserMessageWasEmpty = false;
    this.isClearingQueues = false;
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
    this.isConnecting = false;
    this.isInitializing = false;
    this.reconnectAttempts = 0;
    
    // Limpiar callback de conexión lista
    this.onConnectionReadyCallback = undefined;
  }
  
  /**
   * Controlar si se pueden enviar mensajes
   */
  setCanSendMessage(canSend: boolean): void {
    this.canSendMessage = canSend;
  }
}

export const webSocketService = new WebSocketService();
export default webSocketService;


